import React, { useState, useRef, useEffect } from 'react';
import './App.css';

const CLOUDFRONT_URL = 'https://doi87d66hx6g2.cloudfront.net';

const videos = [
  `${CLOUDFRONT_URL}/videos/pb4720_1.mp4`,
  `${CLOUDFRONT_URL}/videos/Bhur01.mp4`,
  `${CLOUDFRONT_URL}/videos/kallviH01.mp4`,
];

// All 9 unique videos for the music grid (10s thumbnails and full versions)
const gridVideos = [
  { thumb: `${CLOUDFRONT_URL}/videos/Bhur01.mp4`, full: `${CLOUDFRONT_URL}/videos/full/Bhur01.mp4` },
  { thumb: `${CLOUDFRONT_URL}/videos/kallviH01.mp4`, full: `${CLOUDFRONT_URL}/videos/full/kallviH01.mp4` },
  { thumb: `${CLOUDFRONT_URL}/videos/nirya01.mp4`, full: `${CLOUDFRONT_URL}/videos/full/nirya01.mp4` },
  { thumb: `${CLOUDFRONT_URL}/videos/olw_TT01.mp4`, full: `${CLOUDFRONT_URL}/videos/full/olw_TT01.mp4` },
  { thumb: `${CLOUDFRONT_URL}/videos/oodww06StoneBeachSynth2.mp4`, full: `${CLOUDFRONT_URL}/videos/full/oodww06StoneBeachSynth2.mp4` },
  { thumb: `${CLOUDFRONT_URL}/videos/op02_9audio231409.mp4`, full: `${CLOUDFRONT_URL}/videos/full/op02_9audio231409.mp4` },
  { thumb: `${CLOUDFRONT_URL}/videos/pb4720_1.mp4`, full: `${CLOUDFRONT_URL}/videos/full/pb4720_1.mp4` },
  { thumb: `${CLOUDFRONT_URL}/videos/rknn33Sous11.mp4`, full: `${CLOUDFRONT_URL}/videos/full/rknn33Sous11.mp4` },
  { thumb: `${CLOUDFRONT_URL}/videos/sinuBounce01.mp4`, full: `${CLOUDFRONT_URL}/videos/full/sinuBounce01.mp4` },
];

type Page = 'none' | 'bio' | 'links' | 'music';

function App() {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activePage, setActivePage] = useState<Page>('none');
  const [isMuted, setIsMuted] = useState(true);
  const [selectedGridVideoIndex, setSelectedGridVideoIndex] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Redirect paragchordia.com to parag.ai
  useEffect(() => {
    if (window.location.hostname.includes('paragchordia.com')) {
      window.location.replace('https://www.parag.ai' + window.location.pathname);
    }
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }

    // Auto-advance after 10 seconds
    const timer = setTimeout(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
    }, 10000);

    return () => clearTimeout(timer);
  }, [currentVideoIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'm' || e.key === 'M') {
        toggleMute();
        return;
      }

      if (e.key === 'Escape' && selectedGridVideoIndex !== null) {
        setSelectedGridVideoIndex(null);
        return;
      }

      // Arrow keys in music player
      if (selectedGridVideoIndex !== null) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          setSelectedGridVideoIndex((prev) => ((prev ?? 0) + 1) % gridVideos.length);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          setSelectedGridVideoIndex((prev) => ((prev ?? 0) - 1 + gridVideos.length) % gridVideos.length);
        }
        return;
      }

      if (activePage !== 'none') return;

      // Arrow keys on home page
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        setCurrentVideoIndex((prev) => (prev - 1 + videos.length) % videos.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activePage, selectedGridVideoIndex]);


  const openPage = (page: Page) => {
    setActivePage(page);
    setMenuOpen(false);
  };

  const closePage = () => {
    setActivePage('none');
    setSelectedGridVideoIndex(null);
  };

  // Mute and pause background video when music player opens, restore when closed
  const wasMutedRef = useRef(true);
  useEffect(() => {
    if (selectedGridVideoIndex !== null) {
      if (videoRef.current) {
        wasMutedRef.current = videoRef.current.muted;
        videoRef.current.muted = true;
        videoRef.current.pause();
      }
    } else {
      if (videoRef.current) {
        videoRef.current.muted = true;
        setIsMuted(true);
        videoRef.current.play().catch(() => {});
      }
    }
  }, [selectedGridVideoIndex]);

  return (
    <div className="app">
      {/* Fullscreen Video */}
      <video
        ref={videoRef}
        className="video-background"
        src={videos[currentVideoIndex]}
        muted
        playsInline
        autoPlay
        loop
      />

      {/* Menu Icon */}
      <button
        className="menu-icon"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Menu"
      >
        {/* Placeholder for GIF - replace src with your GIF */}
        <div className="menu-icon-placeholder">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </button>

      {/* Dropdown Menu */}
      {menuOpen && (
        <nav className="menu-dropdown">
          <button onClick={() => openPage('bio')}>Bio</button>
          <button onClick={() => openPage('links')}>Links</button>
          <button onClick={() => openPage('music')}>Music</button>
        </nav>
      )}

      {/* Page Overlays */}
      {activePage !== 'none' && (
        <div className="page-overlay" onClick={closePage}>
          <div className="page-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={closePage}>
              &times;
            </button>

            {activePage === 'bio' && (
              <div className="page-bio">
                <p>
                  Parag Chordia is a musician and entrepreneur living in the Bay
                  Area. He has built several companies at the intersection of art
                  and technology with his wife{' '}
                  <a href="https://prernagupta.com" target="_blank" rel="noopener noreferrer">
                    Prerna Gupta
                  </a>{' '}
                  — Khush (AI music tech) acquired by Smule, Hooked (chat fiction)
                  and mysterious (stealth). Previously he founded the NSF-funded
                  Music Intelligence Lab at Georgia Tech where he was a professor.
                  He received his PhD in ML+Music from Stanford and his BS in
                  Applied Mathematics from Yale.
                </p>
              </div>
            )}

            {activePage === 'links' && (
              <div className="page-links">
                <ul>
                  <li><a href="https://x.com/indianmusic" target="_blank" rel="noopener noreferrer">@indianmusic</a></li>
                  <li><a href="https://www.instagram.com/paragchordia/" target="_blank" rel="noopener noreferrer">Instagram</a></li>
                </ul>
              </div>
            )}

            {activePage === 'music' && (
              <div className="page-music">
                {selectedGridVideoIndex !== null ? (
                  <div className="video-player-overlay" onClick={() => setSelectedGridVideoIndex(null)}>
                    <video
                      key={selectedGridVideoIndex}
                      className="player-video"
                      src={gridVideos[selectedGridVideoIndex].full}
                      autoPlay
                      loop
                      playsInline
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button className="player-close" onClick={() => setSelectedGridVideoIndex(null)}>
                      &times;
                    </button>
                  </div>
                ) : (
                  <div className="video-grid">
                    {gridVideos.map((video, index) => (
                      <video
                        key={index}
                        className="grid-video"
                        src={video.thumb}
                        muted
                        autoPlay
                        loop
                        playsInline
                        onClick={() => setSelectedGridVideoIndex(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sound Toggle */}
      <button className="sound-toggle" onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'}>
        {isMuted ? (
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          </svg>
        )}
      </button>

      {/* Video Progress Dots */}
      <div className="video-dots">
        {videos.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentVideoIndex ? 'active' : ''}`}
            onClick={() => setCurrentVideoIndex(index)}
            aria-label={`Video ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default App;
